import React, { useState } from 'react';
import {
    Box,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Grid,
    Typography,
    Button,
    CircularProgress,
    Stack,
    Divider
} from "@mui/material";

import { useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import API from '../../../../axios';
import { InfoHeading, InputField, SearchComboBox, SelectBox } from "../../../../components";
import { openPopUp } from '../../../../store/reducer';
import { errorsSetter } from '../../../../helpers/errors-setter';

function ComponentForm({ handleClose }) {
    const _user = useSelector((state) => state.storeReducer.user);
    const _userRole = useSelector((state) => state.storeReducer.user.role);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        type: null,
        component: null,
        manufacturer: null,
        modal: null,
        organization_id: null,
        name: '',
        item_number: '',
        description: '',
        battery_storage: 0,
        energy_management: 0,
        photovoltaic: 0,
    });
    const [manufacturerItems, setManufacturerItems] = useState([]);
    const [modalItems, setModalItems] = useState([]);
    const [formErrors, setFormErrors] = useState({});
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData({ ...formData, [name]: value })
    }

    const handleSwitchChange = (e) => {
        const { name, checked } = e.target
        setFormData({ ...formData, [name]: checked })
    }

    const handleComponent = (item) => {
        setFormData({ ...formData, type: item, component: item });
        if (item?.is_custom) { return; }
        let _manufacturer = [];
        if (Boolean(item?.manufacturers?.length)) {
            item?.manufacturers.forEach((x, _) => {
                return _manufacturer.push({ label: x.manufacturer, value: x.id });
            });
            setManufacturerItems(_manufacturer);
        } else {
            setManufacturerItems([{ label: 'no items' }]);
            dispatch(openPopUp({
                message: 'Manufacturers are not present in this component.',
                type: 'warning',
            }));
        }
    }

    const handleModal = (e) => {
        const _id = e.target.value;
        setFormData({ ...formData, manufacturer: _id });
        let _index = formData.component.manufacturers.findIndex(x => x.id === _id);
        let _obj = formData?.component?.manufacturers[_index];
        let _modalItems = [];
        if (Boolean(_obj.modals?.length)) {
            _obj?.modals.forEach((x, _) => {
                return _modalItems.push({ label: x.title, value: x.id });
            });
            setModalItems(_modalItems);
        } else {
            setModalItems([{ label: 'no items' }]);
            dispatch(openPopUp({
                message: 'Modals are not present in this Manufacturer.',
                type: 'warning',
            }));
        }
        return
    }

    const handleForm = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        var _data = {};
        if (formData.type?.is_custom) {
            _data = {
                component_type_id: formData.type?.id,
                organization_id: _user?.organization_id,
                name: formData.name,
                item_number: formData.item_number ?? '',
                description: formData.description ?? '',
                battery_storage: formData.battery_storage ? '1' : '0',
                energy_management: formData.energy_management ? '1' : '0',
                photovoltaic: formData.photovoltaic ? '1' : '0',
                is_custom: 1,
                type: 'custom',
            }
        } else {
            _data = {
                component_type_id: formData.type?.id,
                organization_id: _user?.organization_id,
                manufacturer_id: formData.manufacturer,
                modal_id: formData.modal,
                item_number: formData.item_number ?? '',
            }
        }
        setFormErrors({});
        try {
            let { data } = await API(`${_userRole}/components`, 'post', _data);
            dispatch(openPopUp({
                message: 'Component has been created successfully.',
                type: 'success',
            }));
            navigate(`/library/components/${data.id}`, { id: data.id });
        } catch (error) {
            setIsLoading(false);
            setFormErrors(errorsSetter(error));
            dispatch(openPopUp({
                message: 'Error Occurred. While creating component.',
                type: 'error'
            }));
        }
    }

    return (
        <>
            <Box
                component='form'
                autoCapitalize="off"
                autoComplete="off"
                onSubmit={handleForm}
                sx={{
                    pb: 4,
                    px: 1
                }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h6" component="h5">
                            ADD NEW COMPONENT
                        </Typography>
                        <Box component="small" sx={{ color: '#969696' }}>
                            Select the manufacturer and type of component you want
                        </Box>
                    </Grid>
                    <Grid item md={4} xs={12}>
                        <SearchComboBox
                            label='Component Type'
                            fullWidth
                            url={`${_userRole}/component-types?active=1&`}
                            objLabel='title'
                            required
                            size='small'
                            handleChange={handleComponent}
                        />
                    </Grid>
                    <Grid item md={4} xs={12}>
                        {!!formData.type && !formData.type?.is_custom ?
                            <SelectBox
                                items={manufacturerItems}
                                label='Manufacturer'
                                disabled={Boolean(!manufacturerItems.length)}
                                required
                                fullWidth
                                size='small'
                                handleChange={handleModal}
                                error={formErrors?.manufacturer}
                            />
                            :
                            <InputField
                                size="small"
                                fullWidth
                                required
                                label='Name'
                                name='name'
                                handleChange={handleChange}
                                error={formErrors?.name}
                            />
                        }
                    </Grid>
                    <Grid item md={4} xs={12}>
                        {!!formData.type && !formData.type?.is_custom ?
                            <SelectBox
                                items={modalItems}
                                label='Modal'
                                disabled={Boolean(!modalItems.length)}
                                required
                                fullWidth
                                size='small'
                                handleChange={(e) => setFormData({ ...formData, modal: e.target.value })}
                                error={formErrors?.modal}
                            />
                            :
                            <InputField
                                size="small"
                                fullWidth
                                label='Item Number (optional)'
                                type="number"
                                inputProps={{
                                    'min': '0'
                                }}
                                name='item_number'
                                handleChange={handleChange}
                                error={formErrors?.item_number}
                            />
                        }
                    </Grid>
                    {!!formData.type && !formData.type?.is_custom ?
                        <Grid item md={4} xs={12}>
                            <InputField
                                size="small"
                                fullWidth
                                label='Item Number (optional)'
                                type="number"
                                inputProps={{
                                    'min': '0'
                                }}
                                handleChange={handleChange}
                                name='item_number'
                                error={formErrors?.item_number}
                            />
                        </Grid>
                        : null
                    }
                    {!!formData.type && !formData.type?.is_custom ?
                        null
                        :
                        <>
                            <Grid item xs={12}>
                                <InputField
                                    size="small"
                                    fullWidth
                                    label='Description (optional)'
                                    name='description'
                                    multiline
                                    rows="4"
                                    handleChange={handleChange}
                                    error={formErrors?.description}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack
                                    direction='row'
                                    spacing={1}
                                    alignItems='center'
                                >
                                    <InfoHeading variant='h6' text='Technology Affiliation' />
                                </Stack>
                                <FormGroup>
                                    <FormControlLabel control={<Checkbox color='success' checked={Boolean(formData.energy_management)} name='energy_management' onChange={handleSwitchChange} size='small' />} label="Energy Management" />
                                    <FormControlLabel control={<Checkbox color='success' checked={Boolean(formData.battery_storage)} name='battery_storage' onChange={handleSwitchChange} size='small' />} label="Battery Storage" />
                                    <FormControlLabel control={<Checkbox color='success' checked={Boolean(formData.photovoltaic)} name='photovoltaic' onChange={handleSwitchChange} size='small' />} label="Photovoltaic" />
                                </FormGroup>
                            </Grid>
                        </>
                    }
                    <Grid item xs={12}>
                        <Stack
                            direction='row'
                            spacing={1}
                        >
                            <Button
                                variant="contained"
                                onClick={() => { }}
                                sx={{
                                    backgroundColor: '#000',
                                    '&:hover': {
                                        backgroundColor: '#000'
                                    }
                                }}
                                disabled={isLoading}
                                type="submit"
                            >
                                {isLoading ? (
                                    <CircularProgress
                                        size={18}
                                        sx={{ color: "#fff", mr: 1, size: "12px" }}
                                    />
                                ) : null}
                                Add to
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                color='error'
                                onClick={handleClose}
                            >
                                Abort
                            </Button>
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider />
                        <Typography variant="h6" component="h6" mt={3}>
                            CAN'T FIND THE COMPONENTS YOU ARE LOOKING FOR?
                        </Typography>
                        <Box component="small" sx={{ color: '#969696' }}>
                            Fordern Sie die Registrierung von Komponenten an, indem Sie auf die Schaltfläche unten klicken und uns eine E-Mail an <a href="/#">support@eturnity.com</a> senden.
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}
ComponentForm.defaultProps = {
    handleClose: () => { }
}
export default ComponentForm;